/* eslint-disable jsx-a11y/iframe-has-title */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import React, { useCallback, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { useRouter } from 'next/router'
import { innskraningUrlEnum } from 'stores/onboarding/data'
import { Addresses, User } from 'stores/onboarding/types'

import { useInnskraningSentryLogging } from 'hooks/onboarding/useInnskraningExceptionCapture'
import { useInnskraningOnMount } from 'hooks/onboarding/useInnskraningOnMount'
import { useOnMount } from 'hooks/useOnMount'
import { Properties, Property } from 'types/onboarding'
import {
  InnskraningAnalyticsStepEnum,
  trackInnskraningEvent,
} from 'utils/onboarding/analytics'

import s from './Sign.module.scss'
import { getIFrameFromApi, payloadForIframeProps } from './helperFunctions'
import { StepOne, StepTwo } from './Steps'

type Props = {
  user: User
  selectedPropertiesArray: Properties
  onSubmit: () => void
  mailinglist: boolean
  isKassar: boolean
  propertyDelivered?: boolean | null
  addresses?: Addresses
  offerType: string | null
}

export const Signature = (props: Props) => {
  const {
    user,
    selectedPropertiesArray,
    onSubmit,
    mailinglist,
    isKassar,
    propertyDelivered,
    addresses,
    offerType,
  } = props

  const router = useRouter()
  const { captureException, captureMessage } = useInnskraningSentryLogging()

  const [loading, setLoading] = useState(false)
  const [iframeUrl, setIframeUrl] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [step, setStep] = useState<1 | 2>(1)

  const refPdf = useRef<HTMLDivElement>()
  const [inViewRefPdf, inViewPdf] = useInView()
  const offer = isKassar ? 'kiv-kassar' : 'generic'

  const setPdfRef = useCallback(
    (node: HTMLDivElement) => {
      refPdf.current = node
      inViewRefPdf(node)
    },
    [inViewRefPdf]
  )

  const handleIframeTask = (e: MessageEvent) => {
    switch (e.data.type) {
      // document_signed: Once *A* document has been signed (perhaps one of many)
      // signing_completed: Once all documents are signed
      case 'document_signed':
      case 'signing_completed':
        captureMessage('Document signed', (sentry) => ({
          level: 'info',
          tags: {
            component: Signature.name,
          },
        }))
        onSubmit()
        break

      case 'already_signed':
        captureMessage('Document already signed', () => ({
          tags: {
            component: Signature.name,
          },
          contexts: {
            data: {
              iframeUrl,
            },
            error: {
              message: 'Document already signed',
            },
          },
        }))
        setErrorMessage('Þú hefur nú þegar skrifað undir þetta skjal')
        break

      case 'process_not_found':
        captureMessage('Process not found', () => ({
          tags: {
            component: Signature.name,
          },
          contexts: {
            data: {
              iframeUrl,
            },
            error: {
              message: 'Process not found',
            },
          },
        }))
        setErrorMessage('Villa kom upp í ferli, vinsamlegast reynið aftur.')
        break

      case 'invalid_keys':
        captureMessage('Invalid keys', () => ({
          tags: {
            component: Signature.name,
          },
          contexts: {
            data: {
              iframeUrl,
            },
            error: {
              message: 'Invalid keys',
            },
          },
        }))
        setErrorMessage(
          'Villa kom upp í ferli, vinsamlegast hafið samband við þjónustuborð.'
        )
        console.error('Invalid keys!')
        break
    }
  }

  const getIframe = async () => {
    trackInnskraningEvent(
      InnskraningAnalyticsStepEnum.SIGNATURE_START_BUTTON_CLICK
    )
    setLoading(true)
    setIframeUrl('')

    const payload = {
      name: user.name,
      ssn: user.ssn,
      properties: selectedPropertiesArray
        .filter(
          (property: Property & { netorkaMeta?: any }) => !property?.netorkaMeta
        )
        .map((property) => {
          return {
            address: property.address,
            postalCode: property.postalCode,
            city: property.city,
            osOrkuleid: property?.product || '',
            fNumber: property.fNumber,
          }
        }),
      mailinglist,
      addresses,
      reference: user.reference,
      propertyDelivered: propertyDelivered ?? null,
      boxAddress:
        addresses?.oldAddress && offer === 'kiv-kassar' // we only want a non-null value when in
          ? `${addresses.oldAddress}, ${addresses.oldPostalCode}`
          : null,
      email: user.email,
      phoneNumber: user.phoneNumber,
      offerType,
      offer,
      osNotkunarstadir: selectedPropertiesArray
        .filter(
          (property: Property & { netorkaMeta?: any }) =>
            !!property?.netorkaMeta
        )
        .map((property: Property & { netorkaMeta?: any }) => ({
          ...property.netorkaMeta,
          osOrkuleid: property?.product || '',
        })),
    } as payloadForIframeProps

    const captureError = (error: any) => {
      captureException(error, () => ({
        tags: {
          component: Signature.name,
        },
        contexts: {
          error: {
            message: 'Gat ekki náð sambandi við undirskriftarþjónustu',
          },
        },
      }))
    }
    getIFrameFromApi({
      payload,
      inViewPdf,
      refPdf,
      setIframeUrl,
      setStep,
      setErrorMessage,
      setLoading,
      captureError,
    })
  }

  const renderContent = (currentStep: typeof step) => {
    switch (currentStep) {
      case 1:
        return (
          <StepOne
            loading={loading}
            errorMessage={errorMessage}
            getIframe={getIframe}
          />
        )
      case 2:
        return (
          <StepTwo
            selectedPropertiesArray={selectedPropertiesArray}
            setPdfRef={setPdfRef}
            errorMessage={errorMessage}
            loading={loading}
          >
            {iframeUrl && <iframe className={s.sign__iframe} src={iframeUrl} />}
          </StepTwo>
        )
    }
  }

  useInnskraningOnMount(() => {
    window.addEventListener('message', handleIframeTask)

    if (!user.email) {
      router.push(innskraningUrlEnum.LOGIN, innskraningUrlEnum.SUMMARY)
    } else if (!selectedPropertiesArray.length) {
      router.push(innskraningUrlEnum.LOGIN, innskraningUrlEnum.ADDRESS)
    }

    return () => {
      window.removeEventListener('message', handleIframeTask)
    }
  })

  useOnMount(() => {
    trackInnskraningEvent(InnskraningAnalyticsStepEnum.SIGNATURE_PAGELOAD)
  })

  return (
    <div className={s.sign}>
      <div className={s.sign__wrapper}>{renderContent(step)}</div>
    </div>
  )
}
